import { ResponsiveBar } from "@nivo/bar";
import { useGetList } from "ra-core";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useConfigurationContext } from "../root/ConfigurationContext";
import {
  DateRangeSelect,
  ReportFilterBar,
  ReportPageHeader,
  rangeStart,
  useReportFilters,
} from "./ReportChrome";
import type { Contact, ContactTouch, Deal } from "../types";

/**
 * Revenue attribution - the "prove marketing drove revenue" screen.
 * Joins every deal to its contacts' touch journeys and credits pipeline and
 * closed-won dollars to channels/sources/campaigns/landing pages under a
 * selectable model. All real math on captured touches; ad-spend joins (cost
 * per opportunity, ROAS) sync from Google/LinkedIn Ads at deployment.
 */

type Model = "first" | "last" | "linear";
type Dimension = "channel" | "source" | "campaign" | "landingPage";

const MODELS: { id: Model; label: string; hint: string }[] = [
  { id: "first", label: "First touch", hint: "100% credit to the touch that started the journey" },
  { id: "last", label: "Last touch", hint: "100% credit to the touch closest to the deal" },
  { id: "linear", label: "Linear", hint: "credit split evenly across every touch" },
];

const DIMENSIONS: { id: Dimension; label: string }[] = [
  { id: "channel", label: "Channel" },
  { id: "source", label: "Source" },
  { id: "campaign", label: "Campaign" },
  { id: "landingPage", label: "Landing page" },
];

const CHANNEL_LABELS: Record<string, string> = {
  organic: "Organic search",
  paid: "Paid",
  social: "Social",
  email: "Email",
  direct: "Direct",
  referral: "Referral",
};

const PIPELINE_COLOR = "#2e6ba9";
const WON_COLOR = "#b07d3f";

interface Row {
  key: string;
  deals: number;
  pipeline: number;
  won: number;
}

const FILTER_DEFAULTS = {
  model: "first",
  dimension: "channel",
  range: "all",
  stage: "all",
};

export const AttributionTab = () => {
  const { currency } = useConfigurationContext();
  const filters = useReportFilters(FILTER_DEFAULTS);
  const model = (filters.get("model") as Model) || "first";
  const dimension = (filters.get("dimension") as Dimension) || "channel";
  const range = filters.get("range");
  const stageScope = filters.get("stage");
  const setModel = (m: Model) => filters.set("model", m);
  const setDimension = (d: Dimension) => filters.set("dimension", d);
  const [selected, setSelected] = useState<string | null>(null);

  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1000 },
  });

  const { rows, creditedDeals } = useMemo(() => {
    const contactById = new Map(contacts.map((c) => [c.id, c]));
    const buckets = new Map<string, Row>();
    const creditedDeals: {
      deal: Deal;
      credits: { key: string; weight: number }[];
    }[] = [];

    const keyOf = (t: ContactTouch) => {
      const raw =
        dimension === "channel"
          ? t.channel
          : dimension === "source"
            ? t.source
            : dimension === "campaign"
              ? t.campaign
              : t.landingPage;
      if (!raw) return null;
      return dimension === "channel" ? (CHANNEL_LABELS[raw] ?? raw) : raw;
    };

    const start = rangeStart(range);
    for (const deal of deals) {
      if (deal.archived_at) continue;
      // Date range: a deal counts if it was created (open) or closed inside
      // the window - matching "what did this period's marketing create".
      if (start) {
        const anchor = deal.closed_at ?? deal.created_at;
        if (anchor && new Date(anchor) < start) continue;
      }
      if (stageScope === "open" && (deal.stage === "won" || deal.stage === "lost")) continue;
      if (stageScope === "won" && deal.stage !== "won") continue;
      const touches = (deal.contact_ids ?? [])
        .flatMap((id) => contactById.get(id)?.touches ?? [])
        .sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        );
      if (touches.length === 0) continue;

      let credits: { key: string; weight: number }[] = [];
      if (model === "linear") {
        const w = 1 / touches.length;
        const acc = new Map<string, number>();
        for (const t of touches) {
          const k = keyOf(t);
          if (!k) continue;
          acc.set(k, (acc.get(k) ?? 0) + w);
        }
        credits = [...acc.entries()].map(([key, weight]) => ({ key, weight }));
      } else {
        const pick = model === "first" ? touches[0]! : touches.at(-1)!;
        const k = keyOf(pick);
        if (k) credits = [{ key: k, weight: 1 }];
      }
      if (credits.length === 0) continue;
      creditedDeals.push({ deal, credits });

      const isWon = deal.stage === "won";
      const isOpen = deal.stage !== "won" && deal.stage !== "lost";
      for (const { key, weight } of credits) {
        const row = buckets.get(key) ?? { key, deals: 0, pipeline: 0, won: 0 };
        row.deals += weight;
        if (isOpen) row.pipeline += deal.amount * weight;
        if (isWon) row.won += deal.amount * weight;
        buckets.set(key, row);
      }
    }

    const rows = [...buckets.values()].sort(
      (a, b) => b.won + b.pipeline - (a.won + a.pipeline),
    );
    return { rows, creditedDeals };
  }, [deals, contacts, model, dimension, range, stageScope]);

  const fmt = (n: number) =>
    n.toLocaleString("en-US", {
      notation: "compact",
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 1,
    });

  const chartData = [...rows]
    .slice(0, 8)
    .reverse()
    .map((r) => ({
      label: r.key,
      Pipeline: Math.round(r.pipeline),
      "Closed won": Math.round(r.won),
    }));

  const selectedDeals = selected
    ? creditedDeals
        .filter((cd) => cd.credits.some((c) => c.key === selected))
        .sort((a, b) => b.deal.amount - a.deal.amount)
        .slice(0, 12)
    : [];

  const activeModel = MODELS.find((m) => m.id === model);

  return (
    <div className="flex flex-col gap-4">
      <ReportPageHeader
        slug="attribution"
        methodology={
          <ul className="flex flex-col gap-1.5 text-xs leading-5">
            <li>
              <span className="font-medium">First touch</span> - 100% of a
              deal's value is credited to the touch that started the journey.
            </li>
            <li>
              <span className="font-medium">Last touch</span> - 100% goes to
              the touch closest to the deal being created or closed.
            </li>
            <li>
              <span className="font-medium">Linear</span> - credit is split
              evenly across every captured touch on the journey.
            </li>
            <li className="text-muted-foreground pt-1">
              Touches come from form fills, tracked visits, and campaign
              engagement captured on each contact. Ad-spend joins (cost per
              opportunity) activate at deployment.
            </li>
          </ul>
        }
      >
        <ReportFilterBar onReset={filters.reset} showReset={!filters.isDefault}>
          <DateRangeSelect value={range} onChange={(v) => filters.set("range", v)} />
          <Select value={stageScope} onValueChange={(v) => filters.set("stage", v)}>
            <SelectTrigger className="h-8 w-40 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All deals</SelectItem>
              <SelectItem value="open">Open pipeline</SelectItem>
              <SelectItem value="won">Closed won only</SelectItem>
            </SelectContent>
          </Select>
        </ReportFilterBar>
      </ReportPageHeader>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-1">
          {MODELS.map((m) => (
            <button
              key={m.id}
              type="button"
              title={m.hint}
              onClick={() => setModel(m.id)}
              className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                model === m.id
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground mr-1">by</span>
          {DIMENSIONS.map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => {
                setDimension(d.id);
                setSelected(null);
              }}
              className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                dimension === d.id
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>
      {activeModel && (
        <p className="text-xs text-muted-foreground -mt-2">
          {activeModel.label}: {activeModel.hint}.
        </p>
      )}

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* Chart */}
        <Card>
          <CardHeader className="px-4 pb-0">
            <CardTitle className="text-sm">
              Pipeline & closed-won by {DIMENSIONS.find((d) => d.id === dimension)!.label.toLowerCase()}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pt-2">
            {chartData.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No attributable deals yet - touches land as forms and tracking
                capture journeys.
              </p>
            ) : (
              <div style={{ height: Math.max(chartData.length * 52, 200) }}>
                <ResponsiveBar
                  data={chartData}
                  indexBy="label"
                  keys={["Pipeline", "Closed won"]}
                  layout="horizontal"
                  groupMode="grouped"
                  colors={[PIPELINE_COLOR, WON_COLOR]}
                  margin={{ top: 4, right: 24, bottom: 52, left: 120 }}
                  padding={0.3}
                  innerPadding={2}
                  borderRadius={3}
                  enableLabel={false}
                  theme={{
                    text: { fontSize: 11, fill: "var(--muted-foreground)" },
                    grid: { line: { stroke: "var(--border)", strokeWidth: 1 } },
                    tooltip: { container: { background: "transparent", boxShadow: "none", padding: 0 } },
                  }}
                  axisLeft={{ tickSize: 0, tickPadding: 8 }}
                  axisBottom={{ tickSize: 0, tickPadding: 8, format: (v: number) => fmt(v), tickValues: 5 }}
                  tooltip={({ id, value, indexValue }: any) => (
                    <div className="p-2 bg-secondary rounded shadow inline-flex items-center gap-1 text-secondary-foreground text-sm">
                      <strong>{indexValue} · {id}:</strong> {fmt(value)}
                    </div>
                  )}
                  legends={[
                    {
                      dataFrom: "keys",
                      anchor: "bottom",
                      direction: "row",
                      translateY: 46,
                      itemWidth: 110,
                      itemHeight: 16,
                      symbolSize: 10,
                      symbolShape: "circle",
                    },
                  ]}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="px-4 pb-0">
            <CardTitle className="text-sm">
              {DIMENSIONS.find((d) => d.id === dimension)!.label} performance
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-3 font-medium">{DIMENSIONS.find((d) => d.id === dimension)!.label}</th>
                  <th className="py-1 pr-3 font-medium">Deals</th>
                  <th className="py-1 pr-3 font-medium">Pipeline</th>
                  <th className="py-1 pr-3 font-medium">Closed won</th>
                  <th className="py-1 font-medium" title="Ad spend joins from Google/LinkedIn Ads at deployment">
                    Cost / opp
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.key}
                    onClick={() => setSelected(selected === r.key ? null : r.key)}
                    className={`border-t border-border cursor-pointer transition-colors ${
                      selected === r.key ? "bg-primary/5" : "hover:bg-muted/50"
                    }`}
                  >
                    <td className="py-1.5 pr-3 truncate max-w-40">{r.key}</td>
                    <td className="py-1.5 pr-3 tabular-nums">
                      {model === "linear" ? r.deals.toFixed(1) : r.deals}
                    </td>
                    <td className="py-1.5 pr-3 tabular-nums">{fmt(r.pipeline)}</td>
                    <td className="py-1.5 pr-3 tabular-nums">{fmt(r.won)}</td>
                    <td className="py-1.5 text-xs text-muted-foreground">-</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">
              Cost per opportunity fills in when Google / LinkedIn Ads spend
              syncs at deployment. Click a row to see its credited deals.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Drill-down */}
      {selected && (
        <Card>
          <CardHeader className="px-4 pb-0">
            <CardTitle className="text-sm">
              Deals credited to “{selected}” ({MODELS.find((m) => m.id === model)!.label.toLowerCase()})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pt-2 flex flex-col">
            {selectedDeals.map(({ deal, credits }) => {
              const weight = credits.find((c) => c.key === selected)?.weight ?? 0;
              return (
                <Link
                  key={deal.id}
                  to={`/deals/${deal.id}/show`}
                  className="flex items-center gap-3 py-1.5 border-t border-border first:border-t-0 text-sm hover:bg-muted/50 -mx-1 px-1 rounded"
                >
                  <span className="truncate flex-1">{deal.name}</span>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      deal.stage === "won"
                        ? "bg-[#8fb586]/15 text-[#8fb586]"
                        : deal.stage === "lost"
                          ? "bg-[#c97a63]/15 text-[#c97a63]"
                          : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {deal.stage}
                  </span>
                  <span className="tabular-nums text-muted-foreground">
                    {fmt(deal.amount * weight)}
                    {weight < 1 ? ` (${Math.round(weight * 100)}%)` : ""}
                  </span>
                </Link>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
